import mixpanel from 'mixpanel-browser';
import { getUserDeviceInfo } from './utils/mixpanelUtils'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const IS_DEV = import.meta.env.DEV;

// iOS (every browser there runs on WebKit) overflows the Mixpanel
// session-replay recorder's recursive DOM serializer on our deep
// MUI/emotion pages — "RangeError: Maximum call stack size exceeded",
// thrown inside the cross-origin recorder bundle and swallowed by our
// global error handler (Sentry CUSTOM-APP-D: ~91 events, 100% iOS,
// zero app-code frames). WebKit's call-stack limit is far smaller than
// V8's, so the same serialization that passes on Android/desktop blows
// up there. Disable replay on iOS only — analytics events and replay on
// every other platform are unaffected.
const isIOS =
  typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

if (MIXPANEL_TOKEN && !IS_DEV) {
  mixpanel.init(MIXPANEL_TOKEN, {
    api_host: 'https://api-eu.mixpanel.com',
    track_pageview: false,
    record_sessions_percent: isIOS ? 0 : 100,
    record_mask_all_text: true,
    record_mask_all_inputs: true,
  });
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (!import.meta.env.DEV && MIXPANEL_TOKEN) {
    mixpanel.track(eventName, { ...getUserDeviceInfo(), ...properties });
  }
}

// ─── New Taxonomy (14 events) ───────────────────────────────

export const trackSiteOpened = (): void => {
  trackEvent('Site Opened');
};

export const trackTourStarted = (): void => {
  trackEvent('Tour Started');
};

export const trackGateSubmitted = (code: string): void => {
  trackEvent('Gate Submitted', { 'Gate Code': code });
};

export const trackSpaceViewed = (spaceName: string, spaceType: string): void => {
  trackEvent('Space Viewed', {
    'Space Name': spaceName,
    'Space Type': spaceType,
  });
};

export const trackStationViewed = (stationName: string, stationType: string): void => {
  trackEvent('Station Viewed', {
    'Station Name': stationName,
    'Station Type': stationType,
  });
};

export const trackAudioPlayed = (audioName: string): void => {
  trackEvent('Audio Played', { 'Audio Name': audioName });
};

export const trackAudioFinished = (audioName: string): void => {
  trackEvent('Audio Finished', { 'Audio Name': audioName });
};

export const trackVideoPlayed = (videoName: string): void => {
  trackEvent('Video Played', { 'Video Name': videoName });
};

export const trackVideoFinished = (videoName: string): void => {
  trackEvent('Video Finished', { 'Video Name': videoName });
};

export const trackStationSearched = (query: string, found: boolean): void => {
  trackEvent('Station Searched', {
    'Search Query': query,
    'Found': found,
  });
};

export const trackChatMessageSent = (message?: string): void => {
  trackEvent('Chat Message Sent', {
    ...(message ? { 'Message Length': message.length } : {}),
  });
};

export const trackMapOpened = (): void => {
  trackEvent('Map Opened');
};

export const trackLanguageChanged = (from: string, to: string): void => {
  trackEvent('Language Changed', {
    'From Language': from,
    'To Language': to,
  });
};

export const trackErrorOccurred = (errorType: string, errorMessage?: string): void => {
  trackEvent('Error Occurred', {
    'Error Type': errorType,
    ...(errorMessage ? { 'Error Message': errorMessage } : {}),
  });
};

// ─── I amsterdam Card Events ───────────────────────────────

export const trackIAmsterdamGateViewed = (): void => {
  trackEvent('IAC Gate Viewed');
};

export const trackIAmsterdamCardTyped = (): void => {
  trackEvent('IAC Card Typed');
};

export const trackIAmsterdamSubmitted = (): void => {
  trackEvent('IAC Submitted');
};

const IAC_VENDOR_MESSAGE_MAX_LEN = 240;

export interface IacResultAnalyticsExtras {
  iacType?: string | number;
  iacMessage?: string;
  tagIdMasked?: string;
}

export const trackIAmsterdamResult = (
  result: 'success' | 'failure',
  reason?: string,
  iacStatus?: number,
  extras?: IacResultAnalyticsExtras
): void => {
  const rawMsg = extras?.iacMessage;
  const vendorSnippet =
    rawMsg && rawMsg.length > IAC_VENDOR_MESSAGE_MAX_LEN
      ? `${rawMsg.slice(0, IAC_VENDOR_MESSAGE_MAX_LEN)}…`
      : rawMsg;

  trackEvent('IAC Result', {
    'Result': result,
    ...(reason ? { 'Failure Reason': reason } : {}),
    ...(iacStatus !== undefined && iacStatus !== null ? { 'IAC Status Code': iacStatus } : {}),
    ...(extras?.iacType !== undefined && extras?.iacType !== null
      ? { 'IAC Type': String(extras.iacType) }
      : {}),
    ...(vendorSnippet ? { 'IAC Vendor Message': vendorSnippet } : {}),
    ...(extras?.tagIdMasked ? { 'Tag ID Masked': extras.tagIdMasked } : {}),
  });
};

export const trackIAmsterdamModeSwitched = (to: 'iamsterdam' | 'regular'): void => {
  trackEvent('IAC Mode Switched', { 'To Mode': to });
};
